import React, { useEffect, useState } from 'react'
import api from '../../services/api'

interface Coupon {
  id: number
  code: string
  description: string | null
  discount_type: 'percentage' | 'flat'
  discount_value: number
  max_discount: number | null
  max_uses: number | null
  used_count: number
  per_user_limit: number
  min_amount: number | null
  scope_type: 'all' | 'billing_period' | 'plan_type' | 'plan'
  scope_value: string | null
  valid_from: string
  valid_until: string | null
  is_active: boolean
  created_at: string | null
}

interface PlanOption {
  id: number
  name: string
  plan_type: string
  billing_period: string
}

const PLAN_TYPES = ['BASIC', 'STANDARD', 'PREMIUM', 'VIP']
const BILLING_PERIODS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
]

const EMPTY_FORM = {
  code: '',
  description: '',
  discount_type: 'percentage' as 'percentage' | 'flat',
  discount_value: '',
  max_discount: '',
  max_uses: '',
  per_user_limit: '1',
  min_amount: '',
  scope_type: 'all' as Coupon['scope_type'],
  scope_value: '',
  valid_from: new Date().toISOString().slice(0, 16),
  valid_until: '',
}

function scopeLabel(c: Coupon, plans: PlanOption[]): string {
  if (c.scope_type === 'all') return 'All Plans'
  if (c.scope_type === 'billing_period') {
    return BILLING_PERIODS.find(b => b.value === c.scope_value)?.label || c.scope_value || '—'
  }
  if (c.scope_type === 'plan_type') return c.scope_value || '—'
  if (c.scope_type === 'plan') {
    const p = plans.find(pl => String(pl.id) === c.scope_value)
    return p ? `${p.name} (${p.billing_period})` : `Plan #${c.scope_value}`
  }
  return '—'
}

export default function CouponManagement() {
  const [coupons, setCoupons] = useState<Coupon[]>([])
  const [plans, setPlans] = useState<PlanOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')

  const fetchCoupons = async () => {
    try {
      const res = await api.get('/coupons')
      setCoupons(res.data)
    } catch {
      setError('Failed to load coupons')
    } finally {
      setLoading(false)
    }
  }

  const fetchPlans = async () => {
    try {
      const res = await api.get('/admin/plans')
      setPlans(res.data.map((p: any) => ({ id: p.id, name: p.name, plan_type: p.plan_type, billing_period: p.billing_period })))
    } catch {}
  }

  useEffect(() => { fetchCoupons(); fetchPlans() }, [])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.code.trim()) { setFormError('Code is required'); return }
    if (!/^[A-Z0-9_-]{3,20}$/.test(form.code.trim())) {
      setFormError('Code must be 3-20 characters: letters, numbers, hyphens, or underscores only')
      return
    }
    if (!form.discount_value || Number(form.discount_value) <= 0) {
      setFormError('Discount value must be greater than 0')
      return
    }
    if (form.discount_type === 'percentage' && Number(form.discount_value) > 100) {
      setFormError('Percentage discount cannot exceed 100')
      return
    }
    if (form.scope_type !== 'all' && !form.scope_value) {
      setFormError('Select a value for the chosen scope')
      return
    }
    if (form.valid_until && new Date(form.valid_until) <= new Date(form.valid_from)) {
      setFormError('Valid until must be after valid from')
      return
    }
    setSaving(true)
    setFormError('')
    try {
      await api.post('/coupons', {
        code: form.code.trim().toUpperCase(),
        description: form.description || null,
        discount_type: form.discount_type,
        discount_value: Number(form.discount_value),
        max_discount: form.max_discount ? Number(form.max_discount) : null,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        per_user_limit: Number(form.per_user_limit) || 1,
        min_amount: form.min_amount ? Number(form.min_amount) : null,
        scope_type: form.scope_type,
        scope_value: form.scope_type === 'all' ? null : form.scope_value,
        valid_from: new Date(form.valid_from).toISOString(),
        valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
      })
      setForm(EMPTY_FORM)
      setShowForm(false)
      fetchCoupons()
    } catch (err: any) {
      setFormError(err.response?.data?.detail || 'Failed to create coupon')
    } finally {
      setSaving(false)
    }
  }

  const handleToggle = async (coupon: Coupon) => {
    try {
      await api.put(`/coupons/${coupon.id}`, { is_active: !coupon.is_active })
      fetchCoupons()
    } catch {
      setError('Failed to update coupon')
    }
  }

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(p => ({ ...p, [field]: e.target.value }))

  const setScopeType = (scope_type: Coupon['scope_type']) =>
    setForm(p => ({ ...p, scope_type, scope_value: '' }))

  const discountLabel = (c: Coupon) =>
    c.discount_type === 'percentage'
      ? `${c.discount_value}%${c.max_discount ? ` (max ₹${c.max_discount})` : ''}`
      : `₹${c.discount_value}`

  const inputCls = "w-full bg-dark-700 border border-dark-500 text-gray-100 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Coupon Codes</h1>
          <p className="text-sm text-gray-400 mt-1">Create and manage discount codes for subscriptions</p>
        </div>
        <button
          onClick={() => { setShowForm(v => !v); setFormError('') }}
          className="px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg text-sm font-medium transition-colors"
        >
          {showForm ? 'Cancel' : '+ New Coupon'}
        </button>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">{error}</div>
      )}

      {/* Create form */}
      {showForm && (
        <form onSubmit={handleCreate} className="bg-dark-800 border border-dark-600 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-100 mb-4">New Coupon</h2>
          {formError && (
            <div className="bg-red-900/20 border border-red-500/30 text-red-400 px-3 py-2 rounded-md text-sm">{formError}</div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Code *</label>
              <input
                type="text"
                value={form.code}
                onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                placeholder="SUMMER20"
                maxLength={20}
                className={`${inputCls} font-mono tracking-wider`}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Description</label>
              <input
                type="text"
                value={form.description}
                onChange={set('description')}
                placeholder="Summer sale discount"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Discount Type *</label>
              <select value={form.discount_type} onChange={set('discount_type')} className={inputCls}>
                <option value="percentage">Percentage (%)</option>
                <option value="flat">Flat Amount (₹)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">
                Discount Value * {form.discount_type === 'percentage' ? '(%)' : '(₹)'}
              </label>
              <input
                type="number"
                min="0"
                max={form.discount_type === 'percentage' ? '100' : undefined}
                step="0.01"
                value={form.discount_value}
                onChange={set('discount_value')}
                placeholder={form.discount_type === 'percentage' ? '20' : '500'}
                className={inputCls}
                required
              />
            </div>
            {form.discount_type === 'percentage' && (
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Max Discount Cap (₹)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.max_discount}
                  onChange={set('max_discount')}
                  placeholder="2000 (optional)"
                  className={inputCls}
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Min Order Amount (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.min_amount}
                onChange={set('min_amount')}
                placeholder="1000 (optional)"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Max Total Uses</label>
              <input
                type="number"
                min="1"
                value={form.max_uses}
                onChange={set('max_uses')}
                placeholder="100 (leave blank for unlimited)"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Uses Per User (max)</label>
              <input
                type="number"
                min="1"
                value={form.per_user_limit}
                onChange={set('per_user_limit')}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Valid From *</label>
              <input
                type="datetime-local"
                value={form.valid_from}
                onChange={set('valid_from')}
                className={inputCls}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Valid Until</label>
              <input
                type="datetime-local"
                value={form.valid_until}
                onChange={set('valid_until')}
                min={form.valid_from}
                placeholder="Leave blank for no expiry"
                className={inputCls}
              />
            </div>
          </div>

          {/* Scope selector */}
          <div className="border-t border-dark-700 pt-4">
            <label className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2">Applies To</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              {([
                ['all', 'All Plans'],
                ['billing_period', 'Billing Period'],
                ['plan_type', 'Plan Type'],
                ['plan', 'Specific Plan'],
              ] as [Coupon['scope_type'], string][]).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setScopeType(value)}
                  className={`text-xs font-medium px-3 py-2 rounded-lg border transition-colors ${
                    form.scope_type === value
                      ? 'bg-primary-500 border-primary-500 text-white'
                      : 'bg-dark-700 border-dark-600 text-gray-400 hover:border-dark-500'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {form.scope_type === 'billing_period' && (
              <select value={form.scope_value} onChange={set('scope_value')} className={inputCls}>
                <option value="">Select billing period…</option>
                {BILLING_PERIODS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
            )}
            {form.scope_type === 'plan_type' && (
              <select value={form.scope_value} onChange={set('scope_value')} className={inputCls}>
                <option value="">Select plan type…</option>
                {PLAN_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            )}
            {form.scope_type === 'plan' && (
              <select value={form.scope_value} onChange={set('scope_value')} className={inputCls}>
                <option value="">Select a specific plan…</option>
                {plans.map(p => (
                  <option key={p.id} value={p.id}>{p.name} — {p.plan_type} · {p.billing_period}</option>
                ))}
              </select>
            )}
            <p className="text-xs text-gray-600 mt-2">
              {form.scope_type === 'all' && 'This coupon can be used on any plan.'}
              {form.scope_type === 'billing_period' && 'Applies to every plan under the selected billing period, regardless of plan type.'}
              {form.scope_type === 'plan_type' && 'Applies to that plan type across all billing periods (e.g. every Basic plan).'}
              {form.scope_type === 'plan' && 'Applies only to the exact plan selected.'}
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
            >
              {saving ? 'Creating…' : 'Create Coupon'}
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setFormError('') }}
              className="px-4 py-2 bg-dark-700 text-gray-300 rounded-lg text-sm hover:bg-dark-600 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Coupons table */}
      {coupons.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <p className="text-lg">No coupons yet</p>
          <p className="text-sm mt-1">Create your first coupon code above</p>
        </div>
      ) : (
        <div className="bg-dark-800 border border-dark-600 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-dark-700 text-gray-400 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Code</th>
                <th className="px-4 py-3 text-left">Discount</th>
                <th className="px-4 py-3 text-left">Applies To</th>
                <th className="px-4 py-3 text-left">Usage</th>
                <th className="px-4 py-3 text-left">Valid Until</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {coupons.map(coupon => {
                const expired = coupon.valid_until && new Date(coupon.valid_until) < new Date()
                const exhausted = coupon.max_uses !== null && coupon.used_count >= coupon.max_uses
                const effectivelyActive = coupon.is_active && !expired && !exhausted

                return (
                  <tr key={coupon.id} className="hover:bg-dark-700/50 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <span className="font-mono font-semibold text-gray-100 tracking-wider">{coupon.code}</span>
                        {coupon.description && (
                          <p className="text-gray-500 text-xs mt-0.5">{coupon.description}</p>
                        )}
                        {coupon.min_amount && (
                          <p className="text-gray-600 text-xs">Min ₹{coupon.min_amount.toLocaleString('en-IN')}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-primary-400 font-semibold">
                      {discountLabel(coupon)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${coupon.scope_type === 'all' ? 'bg-dark-700 text-gray-400' : 'bg-blue-500/10 text-blue-400'}`}>
                        {scopeLabel(coupon, plans)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`${exhausted ? 'text-red-400' : 'text-gray-300'}`}>
                        {coupon.used_count}
                        {coupon.max_uses !== null && ` / ${coupon.max_uses}`}
                      </span>
                      {coupon.per_user_limit > 1 && (
                        <p className="text-gray-600 text-xs">{coupon.per_user_limit}x per user</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-400">
                      {coupon.valid_until
                        ? new Date(coupon.valid_until).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                        : <span className="text-gray-600">No expiry</span>
                      }
                    </td>
                    <td className="px-4 py-3">
                      {effectivelyActive ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-900/30 text-green-400 text-xs font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                          Active
                        </span>
                      ) : expired ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-900/30 text-yellow-400 text-xs font-medium">
                          Expired
                        </span>
                      ) : exhausted ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-900/30 text-orange-400 text-xs font-medium">
                          Exhausted
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-700 text-gray-400 text-xs font-medium">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleToggle(coupon)}
                        className={`text-xs font-medium px-3 py-1 rounded-md transition-colors ${
                          coupon.is_active
                            ? 'text-red-400 hover:bg-red-900/20'
                            : 'text-green-400 hover:bg-green-900/20'
                        }`}
                      >
                        {coupon.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
